import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import classNames from "classnames";

import { StatusCode } from "api/protocols";
import { GetCustomer, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { PutAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { ICustomer } from "redux/reducers/models/customer";
import { HouseAccountType } from "../../context/contextTypes";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import ChangeCustomer from "components/changeCustomer/ChangeCustomer";
import Drawer from "components/drawerSlide/drawer";
import { Select } from "components/select/index";
import Callout from "components/callout/Callout";

import "./ManageAccountCustomersDrawer.scss";

const MAX_ACCOUNT_CUSTOMERS = 6;

type TCustomerSheetMode = "add" | "replace";

interface IChangeCustomerState {
  showChangeCustomer: boolean;
  showNewCustomer: boolean;
  customerQuery: string;
  customerSearching: boolean;
  customerSearchResult: ICustomer[];
  selectedCustomer: ICustomer | null;
  currentCustomer: ICustomer | null;
  mode: TCustomerSheetMode;
  replaceCustomerId: number | null;
}

interface IProps {
  open: boolean;
  account: HouseAccountType | null | undefined;
  pageCustomer: ICustomer | null | undefined;
  onClose: () => void;
  onSaved: () => void;
}

function getAccountCustomers(account: HouseAccountType | null | undefined): ICustomer[] {
  if (!account) {
    return [];
  }
  if (account.customers && account.customers.length > 0) {
    return account.customers;
  }
  if (account.customer) {
    return [account.customer];
  }
  return [];
}

function getCustomerInitials(customer: ICustomer): string {
  if (customer.initials?.trim()) {
    return customer.initials.trim().slice(0, 2).toUpperCase();
  }
  const first = customer.first_name?.charAt(0) ?? "";
  const last = customer.last_name?.charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  if (initials) {
    return initials;
  }
  return customer.full_name?.trim()?.charAt(0)?.toUpperCase() || "?";
}

function parseCreatedCustomer(body: unknown): ICustomer | null {
  if (body !== null && typeof body === "object") {
    if ("data" in body) {
      const inner = (body as { data: unknown }).data;
      if (inner !== null && typeof inner === "object" && "id" in inner) {
        return inner as ICustomer;
      }
    }
    if ("id" in body) {
      return body as ICustomer;
    }
  }
  return null;
}

export default function ManageAccountCustomersDrawer(props: IProps) {
  const { open, account, pageCustomer, onClose, onSaved } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Option } = Select;

  const [draftCustomers, setDraftCustomers] = useState<ICustomer[]>([]);
  const [showAddSearch, setShowAddSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [limitAlertDismissed, setLimitAlertDismissed] = useState(false);
  const [changeCustomerState, setChangeCustomerState] = useState<IChangeCustomerState>({
    showChangeCustomer: false,
    showNewCustomer: false,
    customerQuery: "",
    customerSearching: false,
    customerSearchResult: [],
    selectedCustomer: null,
    currentCustomer: null,
    mode: "add",
    replaceCustomerId: null,
  });

  const pageCustomerId = pageCustomer?.id;
  const youCustomer = useMemo(
    () => draftCustomers.find(customer => customer.id === pageCustomerId) ?? draftCustomers[0] ?? null,
    [draftCustomers, pageCustomerId],
  );
  const sharedCustomers = useMemo(
    () => draftCustomers.filter(customer => customer.id !== youCustomer?.id),
    [draftCustomers, youCustomer?.id],
  );
  const canAddCustomer = draftCustomers.length < MAX_ACCOUNT_CUSTOMERS;
  const isManageMode = sharedCustomers.length > 0;
  const drawerTitle = isManageMode ? t("Manage customers") : t("Add customer");
  const sheetOverDrawerOpen =
    changeCustomerState.showChangeCustomer || changeCustomerState.showNewCustomer;

  useEffect(() => {
    if (!open) {
      return;
    }
    const customers = getAccountCustomers(account);
    setDraftCustomers(customers);
    setShowAddSearch(customers.filter(customer => customer.id !== pageCustomer?.id).length === 0);
    setLimitAlertDismissed(false);
    setChangeCustomerState(prev => ({
      ...prev,
      showChangeCustomer: false,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
      currentCustomer: null,
      replaceCustomerId: null,
    }));
  }, [open, account, pageCustomer?.id]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;

    if (!open) {
      return;
    }

    timeoutId = setTimeout(() => {
      void (async () => {
        const query = changeCustomerState.customerQuery;
        if (!query) {
          if (mounted) {
            setChangeCustomerState(prev => ({ ...prev, customerSearchResult: [], customerSearching: false }));
          }
          return;
        }

        setChangeCustomerState(prev => ({ ...prev, customerSearching: true }));
        const customerRes = await GetCustomer({ search: query }, false);
        if (!mounted) {
          return;
        }
        if (customerRes.status !== StatusCode.OK) {
          setChangeCustomerState(prev => ({ ...prev, customerSearching: false, customerSearchResult: [] }));
          return;
        }
        setChangeCustomerState(prev => ({
          ...prev,
          customerSearching: false,
          customerSearchResult: Array.isArray(customerRes.data) ? customerRes.data : [],
        }));
      })();
    }, 500);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [changeCustomerState.customerQuery, open]);

  const filteredSearchResults = (changeCustomerState.customerSearchResult ?? []).filter(customer => {
    if (!customer?.id) {
      return false;
    }
    if (changeCustomerState.mode === "replace" && customer.id === changeCustomerState.replaceCustomerId) {
      return true;
    }
    return !draftCustomers.some(existing => existing.id === customer.id);
  });

  function closeDrawer() {
    setShowAddSearch(false);
    onClose();
  }

  function openAddCustomerSearch() {
    if (!canAddCustomer) {
      return;
    }

    setShowAddSearch(true);
    setChangeCustomerState(prev => ({
      ...prev,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
      mode: "add",
    }));
  }

  function addCustomerToDraft(customer: ICustomer) {
    setDraftCustomers(prev => {
      if (prev.some(existing => existing.id === customer.id) || prev.length >= MAX_ACCOUNT_CUSTOMERS) {
        return prev;
      }
      return [...prev, customer];
    });
    setShowAddSearch(false);
    setChangeCustomerState(prev => ({
      ...prev,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
    }));
  }

  function removeCustomerFromDraft(customerId: number) {
    if (customerId === youCustomer?.id) {
      return;
    }
    setDraftCustomers(prev => prev.filter(customer => customer.id !== customerId));
  }

  function openChangeCustomer(customer: ICustomer) {
    setChangeCustomerState(prev => ({
      ...prev,
      showChangeCustomer: true,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      currentCustomer: customer,
      selectedCustomer: null,
      mode: "replace",
      replaceCustomerId: customer.id,
    }));
  }

  function closeChangeCustomer() {
    setChangeCustomerState(prev => ({
      ...prev,
      showChangeCustomer: false,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
      currentCustomer: null,
      replaceCustomerId: null,
      mode: "add",
    }));
  }

  function applyChangeCustomerSelection() {
    const selected = changeCustomerState.selectedCustomer;
    const replaceId = changeCustomerState.replaceCustomerId;
    if (!selected || !replaceId) {
      return;
    }

    setDraftCustomers(prev => {
      if (prev.some(customer => customer.id === selected.id && customer.id !== replaceId)) {
        return prev;
      }
      return prev.map(customer => (customer.id === replaceId ? selected : customer));
    });
    closeChangeCustomer();
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating new customer")));
      return;
    }

    const newCustomer = parseCreatedCustomer(customerRes.data);
    if (!newCustomer) {
      dispatch(showError(t("Error creating new customer")));
      return;
    }

    if (changeCustomerState.showChangeCustomer) {
      setChangeCustomerState(prev => ({
        ...prev,
        showNewCustomer: false,
        selectedCustomer: newCustomer,
      }));
      return;
    }

    addCustomerToDraft(newCustomer);
    setChangeCustomerState(prev => ({ ...prev, showNewCustomer: false }));
  }

  async function saveCustomers() {
    if (!account?.id) {
      return;
    }

    setSaving(true);
    const primaryId =
      draftCustomers.find(customer => customer.id === account.customer_id)?.id ??
      youCustomer?.id ??
      draftCustomers[0]?.id ??
      null;

    const accountRes = await PutAccount(
      {
        id: account.id,
        title: account.title,
        account_number: account.account_number,
        limit: account.limit != null ? Number(account.limit) : undefined,
        accounting_reference_id: account.accounting_reference_id ?? null,
        tournament_id: account.tournament_id ?? null,
        league_id: account.league_id ?? null,
        global: account.global,
        send_statement: account.send_statement,
        auto_pay_balance: account.auto_pay_balance,
        auto_pay_anchor_date: account.auto_pay_anchor_date ?? null,
        default_payment_method_id: account.default_payment_method_id ?? null,
        customer_id: primaryId,
        customer_ids: draftCustomers.map(customer => customer.id),
      },
      true,
    );
    setSaving(false);

    if (accountRes.status !== StatusCode.OK) {
      dispatch(showError(accountRes.message || t("Error saving account")));
      return;
    }

    dispatch(showSuccess(t("Customers updated successfully")));
    onSaved();
    closeDrawer();
  }

  function renderCustomerRow(customer: ICustomer, options?: { isYou?: boolean; showActions?: boolean }) {
    const isYou = !!options?.isYou;
    const showActions = !!options?.showActions;

    return (
      <li key={customer.id} className="manage-account-customers__row">
        <div className="manage-account-customers__identity">
          <div className="manage-account-customers__avatar" aria-hidden>
            {getCustomerInitials(customer)}
          </div>
          <div className="manage-account-customers__details">
            <span className="manage-account-customers__name">
              <span>{customer.full_name || "—"}</span>
              {isYou ? (
                <span className="manage-account-customers__you"> ({t("You")})</span>
              ) : null}
            </span>
            <span className="manage-account-customers__email">{customer.email || "—"}</span>
          </div>
        </div>
        {showActions ? (
          <div className="manage-account-customers__actions">
            <button
              type="button"
              className="manage-account-customers__icon-button manage-account-customers__icon-button--danger"
              aria-label={t("Remove customer")}
              onClick={() => removeCustomerFromDraft(customer.id)}
            >
              <FontAwesomeIcon icon={faTrashCan} />
            </button>
            <button
              type="button"
              className="manage-account-customers__icon-button"
              aria-label={t("Change customer")}
              onClick={() => openChangeCustomer(customer)}
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
          </div>
        ) : null}
      </li>
    );
  }

  const showCustomerSearch = canAddCustomer && (showAddSearch || !isManageMode);

  return (
    <>
      <Drawer
        open={open}
        title={drawerTitle}
        subtitle={t(
          "Add, change or remove customers to share this account's balance, credit limit and transactions.",
        )}
        onClose={closeDrawer}
        onCancel={closeDrawer}
        onOk={() => void saveCustomers()}
        showCancel
        cancelText={t("Cancel")}
        okText={t("Save changes")}
        okDisabled={saving || draftCustomers.length === 0}
        className={classNames("manage-account-customers-drawer", {
          "manage-account-customers-drawer--beneath-sheet": sheetOverDrawerOpen,
        })}
        hideBackdrop={sheetOverDrawerOpen}
        backDropClose
      >
        <div className="manage-account-customers">
          {!canAddCustomer && !limitAlertDismissed ? (
            <div className="manage-account-customers__limit-alert">
              <Callout
                type="alerts"
                iconAlignTop
                title={t("Customer limit reached")}
                content={t(
                  "This house account has reached the maximum of {{max}} customers. Remove one to assign another.",
                  { max: MAX_ACCOUNT_CUSTOMERS },
                )}
                onClose={() => setLimitAlertDismissed(true)}
              />
            </div>
          ) : null}

          <div className="manage-account-customers__section-header">
            <span className="manage-account-customers__section-title">{t("Customers")}</span>
            <span className="manage-account-customers__section-count">
              {draftCustomers.length} / {MAX_ACCOUNT_CUSTOMERS} {t("assigned")}
            </span>
          </div>

          <div className="manage-account-customers__body">
            <ul className="manage-account-customers__list">
              {youCustomer ? renderCustomerRow(youCustomer, { isYou: true }) : null}
            </ul>

            <div className="manage-account-customers__divider" />

            <p className="manage-account-customers__shared-label">{t("Shared with:")}</p>

            {sharedCustomers.length > 0 ? (
              <ul className="manage-account-customers__list">
                {sharedCustomers.map(customer =>
                  renderCustomerRow(customer, { showActions: true }),
                )}
              </ul>
            ) : null}

            {showCustomerSearch ? (
              <div className="manage-account-customers__add-search">
                <Select
                  showSearch
                  label={t("Customer")}
                  className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
                  onSearch={(query: string) =>
                    setChangeCustomerState(prev => ({ ...prev, customerQuery: query, mode: "add" }))
                  }
                  onChange={(_id: number, customer: ICustomer) => addCustomerToDraft(customer)}
                  placeholder={t("Search by name or email address...")}
                  allowClear
                  searchValue={changeCustomerState.customerQuery}
                  showDropDownOnFocus={true}
                  searching={changeCustomerState.customerSearching}
                  autoFocus
                >
                  <div
                    className="ui-select-dropdown-list-item"
                    onClick={() =>
                      setChangeCustomerState(prev => ({
                        ...prev,
                        showNewCustomer: true,
                        mode: "add",
                      }))
                    }
                  >
                    <p>{t("New customer")}</p>
                  </div>
                  {filteredSearchResults.map((customer, index) => (
                    <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
                      <div>
                        <div className="text-semibold text-lg">{customer?.full_name}</div>
                        <div className="text-sm text-gray-500">{customer.email}</div>
                        <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
            ) : canAddCustomer ? (
              <>
                <div className="manage-account-customers__divider" />
                <button
                  type="button"
                  className="manage-account-customers__add-button"
                  onClick={openAddCustomerSearch}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  <span>{t("Add customer")}</span>
                </button>
              </>
            ) : (
              <>
                <div className="manage-account-customers__divider" />
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip
                      id="manage-account-customers-limit-tooltip"
                      className="manage-account-customers__tooltip"
                    >
                      {t("Customer limit reached to")}
                      <br />
                      {t("maximum of {{max}}.", { max: MAX_ACCOUNT_CUSTOMERS })}
                      <br />
                      {t("Remove one to add another.")}
                    </Tooltip>
                  }
                >
                  <span className="manage-account-customers__add-button-wrap">
                    <button type="button" className="manage-account-customers__add-button" disabled>
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      <span>{t("Add customer")}</span>
                    </button>
                  </span>
                </OverlayTrigger>
              </>
            )}
          </div>
        </div>
      </Drawer>

      <ChangeCustomer
        title={t("Change customer")}
        currentCustomerTitle={t("Current customer:")}
        newCustomerTitle={t("Replace with new customer:")}
        okText={t("Change customer")}
        open={changeCustomerState.showChangeCustomer}
        changeCustomerState={changeCustomerState}
        searching={changeCustomerState.customerSearching}
        searchResults={filteredSearchResults}
        newCustomerActive={changeCustomerState.showNewCustomer && changeCustomerState.showChangeCustomer}
        contextLabel={account?.title}
        onCancel={closeChangeCustomer}
        onOk={applyChangeCustomerSelection}
        handleSearchQueryChange={query =>
          setChangeCustomerState(prev => ({ ...prev, customerQuery: query }))
        }
        handleCustomerSelection={(_id, customer) =>
          setChangeCustomerState(prev => ({ ...prev, selectedCustomer: customer }))
        }
        handleRemoveSelectedCustomer={() =>
          setChangeCustomerState(prev => ({
            ...prev,
            selectedCustomer: null,
            customerSearchResult: [],
            customerQuery: "",
          }))
        }
        handleCreateNewCustomer={createNewCustomer}
        handleCloseCreateNewCustomer={() =>
          setChangeCustomerState(prev => ({ ...prev, showNewCustomer: false }))
        }
        handleNewCustomerActive={() =>
          setChangeCustomerState(prev => ({ ...prev, showNewCustomer: true }))
        }
      />

      <NewCustomer
        newCustomerSheetActive={changeCustomerState.showNewCustomer && !changeCustomerState.showChangeCustomer}
        onCancel={() => setChangeCustomerState(prev => ({ ...prev, showNewCustomer: false }))}
        onOk={createNewCustomer}
        searchValue={changeCustomerState.customerQuery}
      />
    </>
  );
}
